import { track } from '@amplitude/marketing-analytics-browser'
import { format } from 'date-fns'
import DOMPurify from 'dompurify'
import moment from 'moment'
import { useState, useEffect, useMemo } from 'react'

import { useCollapse } from 'react-collapsed'
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from 'react-icons/md'
import TextareaAutosize from 'react-textarea-autosize'
import styled from 'styled-components'

import { Title, Desc, FlexAlignCenter } from './CommonStyleComponents'

export interface Collaborator {
  email: string
  firstName: string
  lastName: string
  profileImage: string | null
  username: string | null
  _id: string
}

export interface ConversationMessage {
  id: string
  user: {
    avatar: string
    name: string
  }
  text: string
  datetime: string
  indicatorColor: string
  isMessageOwner: boolean
  isRejected: boolean
  isRejectedMessage: boolean
  isRejectedComment?: string
}

export interface ConversationProps {
  status?: string
  isLoading: boolean
  isSubmitting: boolean
  messages?: {
    postId: string
    messages?: ConversationMessage[]
  }[]
  submitReply: (text: string, postId: string, commentId: string) => void
  canReply?: boolean
  isReplied?: boolean
  isRepliedPosts?: string[]
  isMobile?: boolean
  postId: string
  reviewKey?: string
  fetchComment?: (reviewKey: string, postId: string) => void
  isConversationsEnabled: boolean
  collaborators: Collaborator[]
  isReadOnly: boolean
}

const Conversation = (props: ConversationProps) => {
  const {
    isLoading,
    isSubmitting,
    messages,
    submitReply,
    canReply,
    isReplied: isRepliedDesktop,
    isMobile,
    reviewKey = '',
    postId,
    fetchComment,
    isConversationsEnabled,
    collaborators,
    isReadOnly,
    status,
  } = props

  const [isExpanded, setIsExpanded] = useState(
    status === 'requestChange' && isMobile
  )
  const { getCollapseProps, getToggleProps } = useCollapse({
    defaultExpanded: false,
    isExpanded,
  })

  const [replyMessage, setReplyMessage] = useState('')
  const formatText = (messageText: string, textOnly = false) => {
    // eslint-disable-next-line no-useless-escape
    const regex = /@[\[]\w+[\]]/gm
    return messageText.replace(regex, (match) => {
      const collaborator = collaborators.find((collab: Collaborator) =>
        match.includes(collab._id)
      )
      const name = collaborator ? `${collaborator.username || ''}` : 'unknown'
      return textOnly ? `@{name}` : `<div class="userHighlight">@${name}</div>`
    })
  }

  const handleReplyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.trim().length > 0) {
      setReplyMessage(value)
    }
  }

  const handleReplySubmit = (
    e: React.FormEvent<HTMLFormElement>,
    commentId?: string
  ) => {
    e.preventDefault()

    if (postId && replyMessage && commentId) {
      track('ext-rejection-message-sent')
      submitReply(replyMessage, postId, commentId) // commentId from last msg from /comments
    }
  }

  const currentPost =
    messages && messages.find((item: any) => item.postId === postId)
  const currentPostMessages = (currentPost || {}).messages

  const hasMessages = currentPostMessages && currentPostMessages.length > 0

  const isReplied = useMemo(() => {
    if (isRepliedDesktop) {
      return Boolean(isRepliedDesktop)
    }

    if (status !== 'requestChange' || !hasMessages) {
      return false
    }

    const sortedMessages = currentPostMessages
      .filter((msg) => msg.isRejectedMessage)
      .sort(
        (message, nextMessage) =>
          moment(new Date(message?.datetime)).unix() -
          moment(new Date(nextMessage?.datetime)).unix()
      )
    const lastMessage = sortedMessages?.[sortedMessages.length - 1]

    return Boolean(
      lastMessage &&
        lastMessage.isRejectedComment &&
        lastMessage.isRejectedMessage
    )
  }, [messages, status, postId])

  const getComments = async () => {
    await fetchComment?.(reviewKey, postId)
  }

  useEffect(() => {
    if (isExpanded && !isLoading && reviewKey && postId) {
      getComments()
    }
  }, [isExpanded])

  if (!isMobile && isLoading) return null

  return (
    <Container
      data-testid="conversation-container"
      isMobile={isMobile}
      isConversationsEnabled={isConversationsEnabled}
      onClick={() => {
        if (!isLoading) {
          setIsExpanded(!isExpanded)
        }
      }}
    >
      {isMobile && !isReadOnly ? (
        <TitleWrap {...getToggleProps()}>
          <Title>Conversation</Title>
          <div>
            {isExpanded || isReplied ? (
              <MdOutlineKeyboardArrowUp />
            ) : (
              <MdOutlineKeyboardArrowDown />
            )}
          </div>
        </TitleWrap>
      ) : (
        hasMessages && isConversationsEnabled && <Title>Conversation</Title>
      )}

      <div {...(isMobile ? { ...getCollapseProps() } : {})}>
        <div>
          {(hasMessages || isReplied) && isConversationsEnabled && (
            <>
              <DescStyled isMobile={isMobile}>
                {isMobile && !hasMessages
                  ? 'No conversations'
                  : 'View updates for this post.'}
              </DescStyled>

              <ConversationWrap
                id="conversation_wrap"
                isReplied={isReplied}
                isMobile={isMobile}
              >
                <div>
                  {currentPostMessages?.map(
                    (
                      {
                        id,
                        text,
                        user,
                        datetime,
                        isMessageOwner,
                        isRejected,
                        isRejectedComment,
                        indicatorColor,
                      },
                      index
                    ) =>
                      !isRejected ? (
                        <MessageItemWrap
                          key={datetime + index + id}
                          isMessageOwner={isMessageOwner}
                        >
                          <Row isMessageOwner={isMessageOwner}>
                            {user.avatar && (
                              <Avatar src={user.avatar} alt={user.name} />
                            )}
                            <MessageItem
                              isMessageOwner={isMessageOwner}
                              dangerouslySetInnerHTML={{
                                __html: formatText(DOMPurify.sanitize(text)),
                              }}
                            />
                          </Row>
                          <span>
                            {user.name} at{' '}
                            {format(new Date(datetime), 'd MMM, hh:mm')}
                          </span>
                        </MessageItemWrap>
                      ) : (
                        <StatusChangedMessage
                          key={datetime + index}
                          data-testid={`comment-item-${id}`}
                        >
                          {indicatorColor !== 'transparent' && (
                            <IndicatorBullet bg={indicatorColor} />
                          )}
                          <div
                            className="text_section"
                            dangerouslySetInnerHTML={{
                              __html: formatText(DOMPurify.sanitize(text)),
                            }}
                          ></div>
                          {isRejectedComment && (
                            <div className="comment_section">
                              <div>Comment:</div>
                              <div>
                                <i
                                  dangerouslySetInnerHTML={{
                                    __html: formatText(
                                      formatText(isRejectedComment, true)
                                    ),
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </StatusChangedMessage>
                      )
                  )}
                </div>
                {canReply && !isReplied && postId && (
                  <ReplyArea>
                    <hr />
                    <label htmlFor="change_details">
                      Please add details for your change request.
                    </label>
                    <form
                      onSubmit={(e) => {
                        const lastMessageId =
                          currentPostMessages?.[currentPostMessages.length - 1]
                            ?.id
                        handleReplySubmit(e, lastMessageId)
                      }}
                    >
                      <TextareaAutosize
                        id="change_details"
                        data-testid="change_details"
                        data-textarea-postid={postId}
                        onChange={handleReplyChange}
                        placeholder="Enter details."
                        required
                        onHeightChange={(height) => {
                          // scroll to bottom if textarea height hit a certain limit
                          if (height >= 90) {
                            const conversationWrapElem =
                              document.getElementById('conversation_wrap')
                            conversationWrapElem!.scrollTop =
                              conversationWrapElem!.scrollHeight
                          }
                        }}
                        onClick={(e) => {
                          // mobile
                          e.stopPropagation()
                        }}
                        autoFocus={!isMobile} // can't do native autofocus on mobile because it will have multiple textarea on the page
                      />
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        onClick={(e) => {
                          // mobile
                          e.stopPropagation()
                        }}
                      >
                        Submit
                      </button>
                    </form>
                  </ReplyArea>
                )}
              </ConversationWrap>
            </>
          )}

          {!isConversationsEnabled && canReply && !isReplied && (
            <ReplyWrap>
              <ReplyArea>
                <form
                  onSubmit={(e) => {
                    if (currentPostMessages) {
                      const lastMessageId =
                        currentPostMessages[currentPostMessages.length - 1]
                          ?.id ?? ''
                      handleReplySubmit(e, lastMessageId)
                    }
                  }}
                >
                  <TextareaAutosize
                    id="change_details"
                    data-testid="change_details"
                    data-textarea-postid={postId}
                    onChange={handleReplyChange}
                    placeholder="Enter details."
                    required
                    onClick={(e) => {
                      // mobile
                      e.stopPropagation()
                    }}
                  />
                  <label htmlFor="change_details">
                    Please add details for your change request.
                  </label>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      // mobile
                      e.stopPropagation()
                    }}
                  >
                    Submit
                  </button>
                </form>
              </ReplyArea>
            </ReplyWrap>
          )}

          {isReplied && <i className="detail-sent">Details sent!</i>}
        </div>
      </div>
    </Container>
  )
}

const Container = styled.div<{
  isMobile?: boolean
  isConversationsEnabled?: boolean
}>`
  max-width: ${(props) => (props.isMobile ? '100%' : '360px')};

  .detail-sent {
    color: #fa4f38;
    margin: 10px 0;
    text-align: center;
    display: block;
    ${(props) =>
      props.isMobile &&
      `
    margin-top: -30px;
    `}
  }
`

const DescStyled = styled(Desc)<{ isMobile?: boolean }>`
  margin-bottom: 16px;
  font-weight: 500;

  ${(props) =>
    props.isMobile &&
    `
    text-align: center;
    margin-top: 25px;
    margin-bottom: 0;
  `}
`

const ConversationWrap = styled.div<{
  isMobile?: boolean
  isReplied?: boolean
}>`
  border-radius: 5px;
  padding: 14px;
  height: ${(props) =>
    props.isMobile
      ? 'auto '
      : props.isReplied
        ? 'calc(100vh - 380px)'
        : 'calc(100vh - 360px)'};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${(props) =>
    !props.isMobile &&
    `
  background: #ffffff;
  border: 1px solid #eeeeee;
  `}
`

const ReplyArea = styled.div<{ isConversationsEnabled?: boolean }>`
  hr {
    border: none;
    border-bottom: 1px solid #eeeeee;
    margin: 10px 10px 14px;
  }

  label {
    font-size: 14px;
    color: #fa4f38;
    margin: 0 0 8px 0;
    display: block;
  }

  textarea {
    padding: 8px;
    border-radius: 5px;
    width: 100%;
    height: 52px;
    margin-bottom: 4px;
    min-height: 100px;
    max-height: 200px;

    &::placeholder {
      color: #c4c4c4;
    }
  }

  button {
    padding: 14px 40px;
    border: none;
    font-weight: bold;
    background: #4f31ff;
    color: #ffffff;
    border-radius: 8px;
    display: flex;
    margin-left: auto;
    cursor: pointer;

    &:disabled {
      opacity: 0.7;
      cursor: wait;
    }
  }
`

const ReplyWrap = styled.div`
  textarea {
    margin-bottom: 0;
    min-height: 100px;
    max-height: 200px;
  }
  .detail-sent {
    margin-top: -20px;
  }
`

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 28px;
`

const MessageItemWrap = styled.div<{ isMessageOwner: boolean }>`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  span {
    display: block;
    text-align: ${(props) => (props.isMessageOwner ? 'left' : 'right')};
    font-size: 12px;
    color: #999999;
    margin-top: 5px;
  }

  ${(props) =>
    props.isMessageOwner &&
    `
    margin-left: 38px;
  `}
`

const Row = styled.div<{ isMessageOwner: boolean }>`
  display: flex;
  align-items: center;
  gap: 11px;
  ${(props) =>
    props.isMessageOwner &&
    `flex-direction: row-reverse;
    `};
`

const MessageItem = styled.div<{ isMessageOwner: boolean }>`
  border-radius: 15px 15px 15px 0;
  background: #eeeeee;
  padding: 10px 10px 10px 14px;
  line-height: 20px;
  width: 100%;
  word-break: break-word;

  ${(props) =>
    props.isMessageOwner &&
    `
    border-radius: 15px 15px 0 15px;
    background: #F4F3FF;
  `}

  .userHighlight {
    display: inline;
    color: #4f31ff;
  }
`

const StatusChangedMessage = styled.div`
  margin: 20px 20px 40px 20px;
  border: 1px solid #c4c4c4;
  border-radius: 15px;
  font-size: 12px;
  position: relative;

  .text_section {
    text-align: center;
    line-height: 15px;
    padding: 12px 20px;
  }

  .comment_section {
    background: #fcebe8;
    padding: 12px 20px;
    border-radius: 0 0 15px 15px;
    word-break: break-word;

    > div {
      &:first-child {
        margin-bottom: 4px;
      }
    }
  }
`

const IndicatorBullet = styled.div<{ bg: string }>`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${(props) => props.bg};
  border: 6px solid #ffffff;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
`

const TitleWrap = styled(FlexAlignCenter)`
  justify-content: space-between;

  ${Title} {
    margin-left: 10px;
  }
  svg {
    width: 38px;
    height: auto;
    margin-right: 10px;
  }
`
export default Conversation
